import React, { useState, useEffect } from 'react';
import { ProgressCardsProps, FlashcardSet, StreakData } from '../types';
import { getAllProgress, getAllChapterProgress, getTopicStats, getChapterStats, getAllAudioProgress } from '../utils/firebaseStorage';
import { loadStreakFromFirestore } from '../utils/firestoreStreak';
import { mockFlashcardSets } from '../data/mockData';
import { audiobookChapters } from '../data/audiobookChapters';
import { chaptersIndex, ChapterIndexItem, topics } from '../data/chaptersIndex';
import { Layers, Headphones } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

interface ProgressCardsPropsWithNavigation extends ProgressCardsProps {
  onNavigate?: (section: string, flashcardSetId?: string, audioChapterId?: string) => void;
  userId?: string; // Add userId for streak data loading
}

const ProgressCards: React.FC<ProgressCardsPropsWithNavigation> = ({ progressData, onNavigate, userId }) => {
  const [realFlashcardProgress, setRealFlashcardProgress] = useState<{
    selectedSet: FlashcardSet | null;
    progress: number;
    completed: number;
    total: number;
    knownCount: number;
    unknownCount: number;
    isMainRoundComplete: boolean;
    needsRevision: boolean;
    isFullyCompleted: boolean;
  } | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [realChapterProgress, setRealChapterProgress] = useState<{
    selectedChapter: ChapterIndexItem | null;
    progress: number;
    completedChapters: number;
    totalChapters: number;
    isCompleted: boolean;
    quizScore: number | null;
  } | null>(null);

  const [realAudioProgress, setRealAudioProgress] = useState<{
    currentChapter: string | null;
    currentTime: number;
    duration: number;
    progress: number;
    lastPlayed: Date | null;
  } | null>(null);

  const [streakData, setStreakData] = useState<StreakData | null>(null);

  // Load real flashcard progress from Firebase
  useEffect(() => {
    const loadRealFlashcardProgress = async () => {
      try {
        const allProgress = await getAllProgress();

        // Find the first unfinished set, or any set if all are finished
        let selectedSet: FlashcardSet | null = null;
        let bestProgress = {
          progress: 0,
          completed: false,
          knownCount: 0,
          unknownCount: 0,
          totalCards: 0,
          isMainRoundComplete: false,
          needsRevision: false
        };

        // First, try to find an unfinished set
        for (const set of mockFlashcardSets) {
          const stats = await getTopicStats(set.id);
          if (stats && !stats.completed) {
            selectedSet = set;
            bestProgress = stats;
            break;
          }
        }

        // If all sets are completed, pick the most recently studied one
        if (!selectedSet) {
          let mostRecentDate = 0;
          for (const set of mockFlashcardSets) {
            const progress = allProgress[set.id];
            if (progress) {
              const lastStudiedTime = new Date(progress.lastStudied).getTime();
              if (lastStudiedTime > mostRecentDate) {
                mostRecentDate = lastStudiedTime;
                selectedSet = set;
                const stats = await getTopicStats(set.id);
                if (stats) {
                  bestProgress = stats;
                }
              }
            }
          }
        }

        // If no progress exists at all, pick the first set
        if (!selectedSet) {
          selectedSet = mockFlashcardSets[0];
          bestProgress = {
            progress: 0,
            completed: false,
            knownCount: 0,
            unknownCount: 0,
            totalCards: selectedSet.cards.length,
            isMainRoundComplete: false,
            needsRevision: false
          };
        }

        setRealFlashcardProgress({
          selectedSet,
          progress: bestProgress.progress,
          completed: bestProgress.knownCount + bestProgress.unknownCount,
          total: bestProgress.totalCards,
          knownCount: bestProgress.knownCount,
          unknownCount: bestProgress.unknownCount,
          isMainRoundComplete: bestProgress.isMainRoundComplete,
          needsRevision: bestProgress.needsRevision,
          isFullyCompleted: bestProgress.completed
        });
      } catch (error) {
        console.error('Error loading flashcard progress:', error);
        // Fallback to first set if there's an error
        setRealFlashcardProgress({
          selectedSet: mockFlashcardSets[0],
          progress: 0,
          completed: 0,
          total: mockFlashcardSets[0].cards.length,
          knownCount: 0,
          unknownCount: 0,
          isMainRoundComplete: false,
          needsRevision: false,
          isFullyCompleted: false
        });
      }
    };

    loadRealFlashcardProgress();

    // Load real chapter progress from Firebase
    const loadRealChapterProgress = async () => {
      try {
        const allChapterProgress = await getAllChapterProgress();

        // Find the first unfinished chapter, or any chapter if all are finished
        let selectedChapter: ChapterIndexItem | null = null;
        let completedCount = 0;

        // First, try to find an unfinished chapter
        for (const chapter of chaptersIndex) {
          const stats = await getChapterStats(chapter.id);
          if (stats && stats.completed) {
            completedCount++;
          } else if (!selectedChapter) {
            selectedChapter = chapter;
          }
        }

        // If all chapters are completed, pick the most recently studied one
        if (!selectedChapter) {
          let mostRecentDate = 0;
          for (const chapter of chaptersIndex) {
            const progress = allChapterProgress[chapter.id];
            if (progress && progress.completedAt) {
              const completedTime = new Date(progress.completedAt).getTime();
              if (completedTime > mostRecentDate) {
                mostRecentDate = completedTime;
                selectedChapter = chapter;
              }
            }
          }
        }

        // If no progress exists at all, pick the first chapter
        if (!selectedChapter) {
          selectedChapter = chaptersIndex[0];
        }

        const selectedStats = selectedChapter ? await getChapterStats(selectedChapter.id) : null;

        setRealChapterProgress({
          selectedChapter,
          progress: selectedStats?.readingProgress || 0,
          completedChapters: completedCount,
          totalChapters: chaptersIndex.length,
          isCompleted: selectedStats?.completed || false,
          quizScore: selectedStats?.quizScore || null
        });
      } catch (error) {
        console.error('Error loading chapter progress:', error);
        // Fallback to first chapter if there's an error
        setRealChapterProgress({
          selectedChapter: chaptersIndex[0],
          progress: 0,
          completedChapters: 0,
          totalChapters: chaptersIndex.length,
          isCompleted: false,
          quizScore: null
        });
      }
    };

    loadRealChapterProgress();

    // Load real audio progress from Firebase
    const loadRealAudioProgress = async () => {
      try {
        const allAudioProgress = await getAllAudioProgress();
        const chapters = Object.values(allAudioProgress);

        if (chapters.length === 0) {
          setRealAudioProgress({
            currentChapter: null,
            currentTime: 0,
            duration: 0,
            progress: 0,
            lastPlayed: null
          });
          return;
        }

        // Find the most recently played chapter
        const mostRecent = chapters.reduce((mostRecent, current) => {
          const currentTime = new Date(current.lastPlayed).getTime();
          const mostRecentTime = new Date(mostRecent.lastPlayed).getTime();
          return currentTime > mostRecentTime ? current : mostRecent;
        });

        setRealAudioProgress({
          currentChapter: mostRecent.chapterId,
          currentTime: mostRecent.currentTime,
          duration: mostRecent.duration,
          progress: mostRecent.duration > 0 ? (mostRecent.currentTime / mostRecent.duration) * 100 : 0,
          lastPlayed: new Date(mostRecent.lastPlayed)
        });
      } catch (error) {
        console.error('Error loading audio progress:', error);
        setRealAudioProgress({
          currentChapter: null,
          currentTime: 0,
          duration: 0,
          progress: 0,
          lastPlayed: null
        });
      }
    };

    loadRealAudioProgress();
  }, []);

  // Load streak data from Firestore
  useEffect(() => {
    const loadStreakData = async () => {
      if (!userId) {
        console.log('No userId provided for streak data');
        return;
      }

      try {
        const data = await loadStreakFromFirestore(userId);
        if (data) {
          setStreakData(data);
        } else {
          // Initialize with default streak data if none exists
          setStreakData({
            current: 0,
            longest: 0,
            lastUpdated: new Date(),
            lastActivityDate: new Date().toISOString().split('T')[0],
            milestones: [7, 30, 100],
            milestonesReached: []
          });
        }
      } catch (error) {
        console.error('Error loading streak data:', error);
        // Fallback to default
        setStreakData({
          current: 0,
          longest: 0,
          lastUpdated: new Date(),
          lastActivityDate: new Date().toISOString().split('T')[0],
          milestones: [7, 30, 100],
          milestonesReached: []
        });
      }
    };

    loadStreakData();
  }, [userId]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatStreakText = (days: number): string => {
    if (days === 0) {
      return '0 DNÍ';
    } else if (days === 1) {
      return '1 DEN';
    } else if (days >= 2 && days <= 4) {
      return `${days} DNY`;
    } else {
      return `${days} DNÍ`;
    }
  };



  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {/* Flashcards Progress Card */}
      <div
        className="bg-white dark:bg-dark-card p-6 rounded-xl border border-border shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-md hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer group flex flex-col"
        onClick={() => onNavigate?.('flashcards', realFlashcardProgress?.selectedSet?.id)}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold font-poppins text-light-text-primary dark:text-dark-text-primary group-hover:text-icon-flashcards transition-colors duration-200">Flashkarty</h3>
          <Layers className="text-2xl text-icon-flashcards group-hover:scale-110 transition-transform duration-200 h-8 w-8" />
        </div>

        {realFlashcardProgress ? (
          <>
            {/* Current Set Info */}
            <div className="mb-4">
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">Aktuální téma</div>
              <div className="font-semibold font-poppins text-light-text-primary dark:text-dark-text-primary mb-2">
                {realFlashcardProgress.selectedSet?.title || 'Žádné téma'}
              </div>

              {/* Progress bar */}
              <div className="w-full bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded-full h-2 mb-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${realFlashcardProgress.progress}%` }}
                ></div>
              </div>

              {/* Progress Status */}
              <div className="flex justify-between text-xs text-light-text-secondary dark:text-dark-text-secondary">
                {realFlashcardProgress.isFullyCompleted ? (
                  // Fully completed (both main round and revision done)
                  <span className="flex items-center text-success-green font-medium">
                    <span className="mr-1">✔</span>
                    Dokončeno
                  </span>
                ) : realFlashcardProgress.isMainRoundComplete ? (
                  // Main round complete, but may need revision
                  <div className="flex flex-col space-y-1">
                    <span className="flex items-center text-success-green font-medium">
                      <span className="mr-1">✔</span>
                      Dokončeno
                    </span>
                    {realFlashcardProgress.needsRevision && (
                      <span className="flex items-center text-orange-600 dark:text-orange-400 text-xs">
                        <span className="mr-1">🔁</span>
                        Ještě k opakování: {realFlashcardProgress.unknownCount} {realFlashcardProgress.unknownCount === 1 ? 'karta' : realFlashcardProgress.unknownCount < 5 ? 'karty' : 'karet'}
                      </span>
                    )}
                  </div>
                ) : (
                  // Still in progress through main round
                  <span>{realFlashcardProgress.completed}/{realFlashcardProgress.total} hotovo</span>
                )}
                <span>{realFlashcardProgress.progress}%</span>
              </div>
            </div>

            {/* Statistics */}
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-dark-text-secondary">Známé karty:</span>
                <span className="font-medium text-success-green">{realFlashcardProgress.knownCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-dark-text-secondary">K opakování:</span>
                <span className="font-medium text-orange-600 dark:text-orange-400">{realFlashcardProgress.unknownCount}</span>
              </div>
            </div>
          </>
        ) : (
          /* Loading state */
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-20 h-20 bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded animate-pulse mb-2"></div>
              <div className="h-6 bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded animate-pulse"></div>
            </div>
          </div>
        )}

        <div className="flex-1"></div>
        <button
          className="w-full mt-4 bg-brand-orange text-black py-3 px-4 rounded-lg hover:bg-brand-orange/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-poppins font-medium"
          disabled={!realFlashcardProgress}
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click when button is clicked
            onNavigate?.('flashcards', realFlashcardProgress?.selectedSet?.id);
          }}
        >
          {realFlashcardProgress?.progress === 100 ? 'Opakovat téma' : 'Pokračovat ve studiu'}
        </button>
      </div>

      {/* Audiobook Progress Card */}
      <div
        className="bg-white dark:bg-dark-card p-6 rounded-xl border border-border shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-md hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer group flex flex-col"
        onClick={() => onNavigate?.('audiobook', undefined, realAudioProgress?.currentChapter || undefined)}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold font-poppins text-light-text-primary dark:text-dark-text-primary group-hover:text-icon-audiobook transition-colors duration-200">Audiokniha</h3>
          <Headphones className="text-2xl text-icon-audiobook group-hover:scale-110 transition-transform duration-200 h-8 w-8" />
        </div>

        {realAudioProgress && realAudioProgress.currentChapter ? (
          <div className="mb-4">
            <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">Aktuálně posloucháš</div>
            <div className="font-semibold font-poppins text-light-text-primary dark:text-dark-text-primary mb-2">
              {audiobookChapters.find(ch => ch.id === realAudioProgress.currentChapter)?.title || 'Neznámá kapitola'}
            </div>

            {/* Progress bar */}
            <div className="w-full bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded-full h-2 mb-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${realAudioProgress.progress}%` }}
              ></div>
            </div>

            <div className="flex justify-between text-xs text-light-text-secondary dark:text-dark-text-secondary mb-2">
              <span>{formatTime(realAudioProgress.currentTime)}</span>
              <span>{formatTime(realAudioProgress.duration)}</span>
            </div>

            {/* Current chapter topic info */}
            {(() => {
              const currentChapter = chaptersIndex.find(ch => ch.id === realAudioProgress.currentChapter);
              const currentTopic = currentChapter ? topics.find(t => t.id === currentChapter.topic) : null;
              return (
                <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                  {currentTopic ? (
                    <span>{currentTopic.emoji} {currentTopic.title}</span>
                  ) : (
                    <span>📚 Audiokniha</span>
                  )}
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="mb-4">
            <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">Žádný poslech</div>
            <div className="font-semibold font-poppins text-light-text-primary dark:text-dark-text-primary mb-2">
              Začni poslouchat první kapitolu
            </div>

            {/* Empty progress bar */}
            <div className="w-full bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded-full h-2 mb-2">
              <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: '0%' }}></div>
            </div>

            <div className="flex justify-between text-xs text-light-text-secondary dark:text-dark-text-secondary mb-2">
              <span>0:00</span>
              <span>--:--</span>
            </div>

            {/* Suggested first chapter */}
            <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              🧠 Doporučujeme: Úvod do psychologie
            </div>
          </div>
        )}

        <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          {realAudioProgress?.lastPlayed ? (
            <>
              Naposledy: {realAudioProgress.lastPlayed.toLocaleDateString('cs-CZ')}
              {realAudioProgress.progress > 0 && realAudioProgress.progress < 100 && (
                <span className="block text-xs mt-1 text-success-green">
                  Pokračuj od {Math.round(realAudioProgress.progress)}%
                </span>
              )}
              {realAudioProgress.progress >= 100 && (
                <span className="block text-xs mt-1 text-success-green">
                  ✓ Kapitola dokončena
                </span>
              )}
            </>
          ) : (
            <>Začni svou první audioknihu</>
          )}
        </div>

        <div className="flex-1"></div>
        <button
          className="w-full bg-brand-orange text-black py-3 px-4 rounded-lg hover:bg-brand-orange/90 transition-colors duration-200 font-poppins font-medium"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click when button is clicked
            onNavigate?.('audiobook', undefined, realAudioProgress?.currentChapter || undefined);
          }}
        >
          {realAudioProgress?.currentChapter ? (
            realAudioProgress.progress >= 100 ? 'Vybrat další kapitolu' : 'Pokračovat v poslechu'
          ) : (
            'Začít poslouchat'
          )}
        </button>
      </div>

      {/* Streak Widget Card */}
      <div
        className="bg-white dark:bg-dark-card p-6 rounded-xl border border-border shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-md hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer group flex flex-col"
        onClick={() => onNavigate?.('courses')}
      >
        {streakData ? (
          <>
            {/* Center-aligned content */}
            <div className="flex-1 flex flex-col items-center justify-center space-y-4 py-4">
              {/* Lottie Fire Animation */}
              <div className="w-32 h-32 sm:w-36 sm:h-36">
                <div className={`w-full h-full ${streakData.current === 0 ? 'grayscale opacity-50' : ''}`}>
                  <DotLottieReact
                    src="/materials/12152388.lottie"
                    loop={streakData.current > 0}
                    autoplay={streakData.current > 0}
                    speed={0.7}
                    className="w-full h-full"
                  />
                </div>
              </div>

              {/* Streak Number */}
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
                  {formatStreakText(streakData.current)}
                </div>

                {/* Subtitle */}
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  {streakData.current > 0 ? 'Studijní série' : 'Začni svou sérii!'}
                </div>

                {/* Longest streak info */}
                {streakData.longest > 0 && streakData.longest !== streakData.current && (
                  <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-2">
                    Nejdelší: {formatStreakText(streakData.longest)}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* Loading state */
          <div className="flex-1 flex flex-col items-center justify-center space-y-4 py-4">
            <div className="w-32 h-32 bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded-full animate-pulse"></div>
            <div className="w-24 h-8 bg-light-text-secondary/20 dark:bg-dark-text-secondary/20 rounded animate-pulse"></div>
          </div>
        )}
      </div>


    </div>
  );
};

export default ProgressCards;